import {
  AppKeys,
  DashboardSearchParams,
  DateRangeValue,
  FeatureFlags,
  IdentityFavoriteSource,
  Integration,
  IntegrationsDashboardType,
  PatientMatchStatus,
  ProfessionalBaseType,
  SendingRequestDto,
  SendingsDashboardType,
  SourceType,
  TelecomType,
} from '@domain/types';
import { SuggestionAction } from '@domain/types/entities/irenePrediction';
import { joinValues } from '@filters';
import { CORE_APPS_INFO } from '@launcher/constants';
import { MedicalVisaQuery } from '@lifen-medical-visa/features/dashboard/services';
import { AnalyticsBrowser, Options } from '@segment/analytics-next';
import electronService from '@services/electron/electron.service';
import { EnvManager } from '@services/environment/environment.service';
import { logger } from '@services/logger/logger.service';
import { isEmailDematPatient } from '@utils/document.util';
import { version } from '../../../package.json';
import * as Segments from './typewriter/segment';
import { bindUploadSegmentTracking } from './uploadSegmentTracking';

interface TrackingUserData {
  userLastName: string | undefined;
  userFirstName: string | undefined;
  userEmail: string | undefined;
  userWorkspaceId: string | undefined;
  userId: string | undefined;
  currentApiDomain: string | undefined;
  locale: string | undefined;
}

export enum TrackingEventName {
  AppLoadStart = 'App Load Start',
  AppLoadEnd = 'App Load End',
  AppUpdateCompleted = 'App Update Completed',
  AppUpdateFailed = 'App Update Failed',
  AppUpdateStarted = 'App Update Started',
  AnalysisPollingGaveUp = 'Analysis Polling Gave Up',
  BatchActionClicked = 'Batch Action Clicked',
  BatchDocumentsModified = 'Batch Documents Updated',
  BatchFileUploadEnd = 'Batch File Upload End',
  BatchFileUploadStart = 'Batch File Upload Start',
  CancelDocumentsSending = 'Cancel Documents Sending',
  CancelInfoMessage = 'Cancel Info Message',
  CloseEnsConversation = 'Close Ens Conversation',
  ContactCardViewed = 'Contact Card Viewed',
  CustomEnsMessageAdded = 'Custom Ens Message Added',
  InErrorsDashboardFiltered = 'In Errors Dashboard Filtered',
  DashboardFiltered = 'Dashboard Filtered',
  DashboardSorted = 'Dashboard Sorted',
  DeferredDone = 'Deferred Done',
  DirectorySearchPanelsClicked = 'Directory Search Panels Clicked',
  DirectorySearched = 'Directory Searched',
  DirectoryResultClicked = 'Directory Result Clicked',
  DocumentActionClicked = 'Document Action Clicked',
  DocumentDatePeriodModified = 'Document Date Period Modified',
  DocumentIdCopied = 'Document Id Copied',
  DocumentIntegrated = 'Document Integrated',
  DocumentNavigatorClicked = 'Document Navigator Clicked',
  DocumentOriginalSenderModified = 'Document Original Sender Modified',
  DocumentPrinted = 'Document Printed',
  DocumentSenderModified = 'Document Sender Modified',
  DocumentTitleModified = 'Document Title Modified',
  DocumentTypeModified = 'Document Type Modified',
  DraftDashboardGlobalNavigationLinkClicked = 'Draft Dashboard Global Navigation Link Clicked',
  DraftDashboardIdentityNavigationLinkClicked = 'Draft Dashboard Identity Navigation Link Clicked',
  EncounterListClicked = 'Encounter List Clicked',
  EncounterUpdated = 'Encounter Updated',
  FavoriteAdded = 'Favorite Added',
  FavoriteRemoved = 'Favorite Removed',
  FileImportCompleted = 'File Import Completed',
  FileUploadFailed = 'File Upload Failed',
  FileUploadStarted = 'File Upload Started',
  FileUploaded = 'File Uploaded',
  GroupedDocumentReordered = 'Grouped Document Reordered',
  ImportOriginalDocumentSubmitted = 'Import-original-document Submit Button Clicked',
  ImportOriginalDocumentBack = 'Import-original-document Back Button Clicked',
  ImportModalClosed = 'Import Modal Closed',
  ImportModalExpanded = 'Import Modal Expanded',
  IntegrationMatchDone = 'Integration Match Done',
  MedicalExamTypeModified = 'Medical Exam Type Modified',
  MedicalVisaSigned = 'Medical Visa Signed',
  MessageSent = 'Message Sent',
  NewDematRecipientFormOpened = 'New Demat Recipient Form Opened',
  NewDematRecipientFormSubmitted = 'New Demat Recipient Form Submitted',
  NewDematRecipientFormMatchExistingEmail = 'New Demat Recipient Form Match Existing Email',
  NewDematRecipientFormSubmitExistingEmail = 'New Demat Recipient Form Submit Existing Email',
  NotificationMenuOpened = 'Notification Menu Opened',
  PageAccountCreationViewed = 'Page Account Creation Viewed',
  PageArchiveDashboardViewed = 'Page Archive Dashboard Viewed',
  PageDraftDashboardViewed = 'Page Draft Dashboard Viewed',
  PageErrorDashboardViewed = 'Page Error Dashboard Viewed',
  PageInboxDashboardViewed = 'Page Inbox Dashboard Viewed',
  PageInErrorsDashboardViewed = 'Page In Errors Dashboard Viewed',
  PageIntegratedDashboardViewed = 'Page Integrated Dashboard Viewed',
  PageSentDashboardViewed = 'Page Sent Dashboard Viewed',
  PageWorklistDashboardViewed = 'Page Worklist Dashboard Viewed',
  PatientAdded = 'Patient Added',
  PatientCreateClicked = 'Patient Create Clicked',
  PatientCreated = 'Patient Created',
  PatientDirectoryImportFailed = 'Patient Directory Import Failed',
  PatientDirectoryImportStarted = 'Patient Directory Import Started',
  PatientMatchRelaunched = 'Patient Match Relaunched',
  PatientModifyClicked = 'Patient Modify Clicked',
  PatientRemoved = 'Patient Removed',
  PatientSearched = 'Patient Searched',
  PatientUpdated = 'Patient Updated',
  PdfViewerLongPress = 'PDF Viewer Long Press',
  PdfEditorBack = 'Pdf-editor Back Button Clicked',
  PdfEditorCancel = 'Pdf-editor Cancel Button Clicked',
  PdfEditorCut = 'Pdf-editor Cut Button Clicked',
  PdfEditorUncut = 'Pdf-editor Uncut Button Clicked',
  PdfEditorCutAll = 'Pdf-editor CutAll Button Clicked',
  PdfEditorRotatePage = 'Pdf-editor Rotate Button Clicked',
  PdfEditorRemovePage = 'Pdf-editor RemovePage Button Clicked',
  PdfEditorRestorePage = 'Pdf-editor RestorePage Button Clicked',
  PdfEditorReset = 'Pdf-editor Reset Button Clicked',
  PdfEditorSubmit = 'Pdf-editor Submit Button Clicked',
  PdfEditorZoom = 'Pdf-editor Zoom Button Clicked',
  PdfEditorZoomNavigate = 'Pdf-editor ZoomNavigate Button Clicked',
  PdfEditorPermutatePages = 'Pdf-editor Permutate Pages Button Clicked',
  PdfEditorAcceptSuggestion = 'Pdf-editor Accept Suggestion Button Clicked',
  PdfEditorDismissSuggestion = 'Pdf-editor Dismiss Suggestion Button Clicked',
  PusherDisconnected = 'Pusher Disconnected',
  PusherReconnected = 'Pusher Reconnected',
  PusherEventMissed = 'Pusher Event Missed',
  PdfEditorPageViewed = 'Pdf-editor Page Viewed',
  AddToPendingFolder = 'Add To Pending Folder Button Clicked',
  RevertAddToPendingFolder = 'Revert Add To Pending Folder Button Clicked',
  AddToPendingFolderGotoList = 'Add To Pending Folder Go to List Button Clicked',
  RemoveFromPendingFolder = 'Remove From Pending Folder Button Clicked',
  RevertRemoveFromPendingFolder = 'Revert Remove From Pending Folder Button Clicked',
  RemoveFromPendingFolderGotoList = 'Remove From Pending Folder Go to List Button Clicked',
  RecipientAdded = 'Recipient Added',
  RecipientRemoved = 'Recipient Removed',
  RecipientUpdated = 'Recipient Updated',
  SelectedIdentityUpdated = 'Selected Identity Updated',
  UserForcedLogout = 'User Forced Logout',
  VerificationAction = 'Verification Action',
  OpenConnectorSettings = 'Open Connector Settings',
  SecretConnectionChanged = 'Secret Connection Changed',
  ConnectorSecurityLabelChanged = 'Connector Security Label Changed',
  NetworkConnection = 'Network Connection',
  DocumentSpecialityModified = 'Document Speciality Modified',
  DocumentForwardToLifenDocumentModified = "Document 'Forward To Lifen Document' Modified",
  MedicalVisaCommentClicked = 'Medical Visa Comment Clicked',
  MedicalVisaDashboardFiltered = 'Medical Visa Dashboard Filtered',
  ViewPendingReadyToIntegrateDocumentsClicked = 'View Pending Ready To Integrate Documents Clicked',
  SearchPatientStarted = 'Search Patient Started',
  ApplyPatientResult = 'Apply Patient Result',
}

export class Tracking {
  private readonly version: string = version;

  private featureFlags: string[] | undefined;

  private userId: TrackingUserData['userId'];

  private userEmail: TrackingUserData['userEmail'];

  private userLastName: TrackingUserData['userLastName'];

  private userFirstName: TrackingUserData['userFirstName'];

  private userWorkspaceId: TrackingUserData['userWorkspaceId'];

  private currentApiDomain: TrackingUserData['currentApiDomain'];

  private locale: TrackingUserData['locale'];

  private readonly analytics: AnalyticsBrowser;

  public constructor(analyticsClient: AnalyticsBrowser) {
    this.analytics = analyticsClient;
  }

  /**
   * Wires the standalone `uploadAnalytics` helpers to this instance's Segment client.
   * Must be called once after construction, before any upload tracking helper runs.
   */
  public bindUploadTracking(): void {
    bindUploadSegmentTracking(this.analytics, () => this.options);
  }

  private currentAppOpened: AppKeys;

  private appLoadStartTimeMs: number | undefined;

  public initUserData({
    userId,
    userEmail,
    userWorkspaceId,
    userLastName,
    userFirstName,
    currentApiDomain,
    locale,
  }: TrackingUserData): void {
    this.userId = userId;
    this.userWorkspaceId = userWorkspaceId;
    this.userEmail = userEmail;
    this.userLastName = userLastName;
    this.userFirstName = userFirstName;
    this.currentApiDomain = currentApiDomain;
    this.locale = locale;
    this.initAnalytics();
  }

  public initFeatureFlags(featureFlags: FeatureFlags): void {
    this.featureFlags = Object.entries(featureFlags).reduce(
      (list: string[], [key, value]) => (value === true ? [...list, key] : list),
      [],
    );
  }

  private initAnalytics() {
    this.analytics
      .load(
        {
          writeKey: EnvManager.getSegmentKey() ?? '',
          cdnURL: `https://${EnvManager.getBaseUrlProxy()}/private/proxy/cdnsegment`,
        },
        {
          integrations: {
            'Segment.io': {
              apiHost: `${EnvManager.getBaseUrlProxy()}/private/proxy/segment`,
            },
          },
        },
      )
      .catch((err) => logger.error('LOGIC', `Failed to load analytics`, err));
  }

  private getCurrentApp() {
    return Object.values(CORE_APPS_INFO).find((app) => window.location.pathname.includes(app.path))
      ?.name;
  }

  private async getBuild(): Promise<string | undefined> {
    if (!window.electron) return undefined;
    try {
      return await electronService.getElectronVersion();
    } catch (_e) {
      return undefined;
    }
  }

  private get options(): Options {
    return {
      context: {
        screen: {
          width: window.screen.width.toString(),
          height: window.screen.height.toString(),
        },
      },
      traits: {
        currentWorkspaceFhirReference: this.userWorkspaceId,
        currentApp: this.getCurrentApp(),
        launcherVersion: this.version,
        locale: this.locale,
      },
    };
  }

  private async getOSInfo(): Promise<{ osType: string; osRelease: string }> {
    if (window.electron) {
      return {
        osType: await electronService.getOsType?.(),
        osRelease: await electronService.getOsRelease?.(),
      };
    }

    const userAgent = navigator.userAgent;
    let osType = 'Unknown';

    if (userAgent.indexOf('Win') !== -1) osType = 'Windows';
    else if (userAgent.indexOf('Mac') !== -1) osType = 'MacOS';
    else if (userAgent.indexOf('Linux') !== -1) osType = 'Linux';
    else if (userAgent.indexOf('Android') !== -1) osType = 'Android';
    else if (userAgent.indexOf('iOS') !== -1) osType = 'iOS';

    return {
      osType,
      osRelease: userAgent,
    };
  }

  public identify(): void {
    try {
      this.analytics.identify(
        this.userId || '',
        {
          firstName: this.userFirstName, // required for zendesk integration
          lastName: this.userLastName, // required for zendesk integration
          email: this.userEmail,
          featureFlags: this.featureFlags,
          currentWorkspaceFhirReference: this.userWorkspaceId,
          lifenDomain: this.currentApiDomain,
          locale: this.locale,
        },
        // Intercom is booted directly by `services/intercom`, with a JWT rather than the HMAC
        // `user_hash` this device-mode destination expects. Sending both makes Intercom 400.
        { Intercom: false },
      );
    } catch (_error) {}
  }

  public async trackClientSession(): Promise<void> {
    logger.info('LOGIC', 'Tracking - trackClientSession');
    try {
      const isElectron = !!window.electron;
      const osInfo = await this.getOSInfo();
      const sessionProperties = {
        clientType: isElectron ? 'electron' : 'browser',
        launcherVersion: this.version,
        ...osInfo,
        ...(isElectron && {
          desktopVersion: await this.getBuild(),
        }),
      } as const;
      this.analytics.track('Client Session Started', sessionProperties);
    } catch (_error) {}
  }

  public trackAppLoadStart(): void {
    if (this.appLoadStartTimeMs !== undefined) return;
    this.appLoadStartTimeMs = performance.now?.() ?? Date.now();
    this.analytics.track(TrackingEventName.AppLoadStart, {}, this.options);
  }

  public trackAppLoadEnd(): void {
    const end = performance.now?.() ?? Date.now();
    const durationMs = this.appLoadStartTimeMs
      ? Math.max(0, Math.round(end - this.appLoadStartTimeMs))
      : undefined;
    this.appLoadStartTimeMs = undefined;
    this.analytics.track(TrackingEventName.AppLoadEnd, { durationMs }, this.options);
  }

  public trackDraftDashboardNavClick(identity?: string): void {
    if (identity) {
      const props: Segments.DraftDashboardIdentityNavigationLinkClicked = {
        identity,
      };
      this.analytics.track(
        TrackingEventName.DraftDashboardIdentityNavigationLinkClicked,
        props,
        this.options,
      );
    } else {
      this.analytics.track(
        TrackingEventName.DraftDashboardGlobalNavigationLinkClicked,
        {},
        this.options,
      );
    }
  }

  public trackPageViewed(
    // TODO: Create a type for this
    dashboardType:
      | SendingsDashboardType
      | IntegrationsDashboardType
      | 'Notification'
      | 'Error'
      | 'InErrors'
      | 'Pending'
      | 'ErrorsAndDuplicates',
  ): void {
    switch (dashboardType) {
      case SendingsDashboardType.Archive:
        this.analytics.track(TrackingEventName.PageArchiveDashboardViewed, {}, this.options);
        break;
      case SendingsDashboardType.Draft:
        this.analytics.track(TrackingEventName.PageDraftDashboardViewed, {}, this.options);
        break;
      case SendingsDashboardType.Inbox:
        this.analytics.track(TrackingEventName.PageInboxDashboardViewed, {}, this.options);
        break;
      case SendingsDashboardType.Sent:
        this.analytics.track(TrackingEventName.PageSentDashboardViewed, {}, this.options);
        break;
      case IntegrationsDashboardType.Worklist:
        this.analytics.track(TrackingEventName.PageWorklistDashboardViewed, {}, this.options);
        break;
      case IntegrationsDashboardType.Integrated:
        this.analytics.track(TrackingEventName.PageIntegratedDashboardViewed, {}, this.options);
        break;
      case 'Notification':
        this.analytics.track(TrackingEventName.NotificationMenuOpened, {}, this.options);
        break;
      case 'Error':
        this.analytics.track(TrackingEventName.PageErrorDashboardViewed, {}, this.options);
        break;
      case 'InErrors':
        this.analytics.track(TrackingEventName.PageInErrorsDashboardViewed, {}, this.options);
        break;
      default:
        break;
    }
  }

  public messageDocumentSent(document: SendingRequestDto, isBatchAction?: boolean): void {
    const { patient, documentsMerged, integration, sender } = document;
    const props: Segments.MessageSent = {
      communicationRequest: document.id,
      documentLoincType: document.documentType,
      dematPatient: isEmailDematPatient(document),
      senderFhirReference: `${sender.type}/${sender.id}`,
      isBatchAction,
    };

    if (documentsMerged && documentsMerged.length > 1) {
      props.isGrouped = document.documentsMerged ? document.documentsMerged.join(' ,') : undefined;
    }

    if (patient?.telecoms && patient?.telecoms.length > 1) {
      props.isDirectMail = true;
    }

    if ((integration as Integration)?.connectors.DMP) {
      let status = null;

      status = (integration as Integration).connectors.DMP?.isSelected
        ? Segments.DmpIntegration.Selected
        : Segments.DmpIntegration.Unselected;

      props.dmpIntegration = status;
    }

    if ((integration as Integration)?.connectors.EHR) {
      let status = Segments.DPIIntegration.Unselected;
      if ((integration as Integration).connectors.EHR?.isSelected) {
        status =
          (integration as Integration).connectors.EHR?.integrationMode === 'auto'
            ? Segments.DPIIntegration.Auto
            : Segments.DPIIntegration.Manual;
      }

      props.dpiIntegration = status;
    }

    this.analytics.track(TrackingEventName.MessageSent, props, this.options);
  }

  public documentRecipientUpdated({
    communicationRequest,
    fhirReference,
    telecoms,
    type,
    source,
  }: {
    communicationRequest: string;
    fhirReference: string;
    telecoms: TelecomType[];
    type: ProfessionalBaseType | 'Patient';
    source: SourceType | undefined;
  }) {
    this.analytics.track(
      TrackingEventName.RecipientUpdated,
      { communicationRequest, fhirReference, telecoms, type, source },
      this.options,
    );
  }

  public documentRecipientAdded({
    communicationRequest,
    fhirReference,
    telecoms,
    type,
    source,
  }: {
    communicationRequest: string;
    fhirReference: string;
    telecoms: TelecomType[];
    type: ProfessionalBaseType | 'Patient';
    source: SourceType | undefined;
  }) {
    this.analytics.track(
      TrackingEventName.RecipientAdded,
      { communicationRequest, fhirReference, telecoms, type, source },
      this.options,
    );
  }

  public documentRecipientRemoved({
    communicationRequest,
    fhirReference,
    source,
  }: {
    communicationRequest: string;
    fhirReference: string;
    source: SourceType | undefined;
  }) {
    this.analytics.track(
      TrackingEventName.RecipientRemoved,
      { communicationRequest, fhirReference, source },
      this.options,
    );
  }

  public favoriteAdded({
    addedFrom,
    fhirReference,
    type,
  }: {
    addedFrom: IdentityFavoriteSource;
    fhirReference?: string;
    type?: ProfessionalBaseType;
  }) {
    this.analytics.track(
      TrackingEventName.FavoriteAdded,
      { fhirReference, type, addedFrom },
      this.options,
    );
  }

  public favoriteRemoved({
    removedFrom,
    fhirReference,
    type,
  }: {
    removedFrom: IdentityFavoriteSource;
    fhirReference?: string;
    type?: ProfessionalBaseType;
  }) {
    this.analytics.track(
      TrackingEventName.FavoriteRemoved,
      { fhirReference, type, removedFrom },
      this.options,
    );
  }

  public trackCancelInfoMessage({ payloadType, id }: { payloadType: string; id: string }) {
    this.analytics.track(TrackingEventName.CancelInfoMessage, { payloadType, id }, this.options);
  }

  public trackDeferredDone({ payloadType, id }: { payloadType: string; id: string }) {
    this.analytics.track(TrackingEventName.DeferredDone, { payloadType, id }, this.options);
  }

  // Lifen Integration
  public messageDocumentIntegrated(props: Segments.DocumentIntegrated): void {
    this.analytics.track(TrackingEventName.DocumentIntegrated, props, this.options);
  }

  public dashboardFiltered({
    patientFiltered,
    patientAdvancedFiltered,
    servicesFiltered,
    sourceFiltered,
    statusFiltered,
    mediumFiltered,
    page,
    dashboardType,
    recipient,
    dateRange,
    defaultDateRange,
    hideOtherRequesters,
    signingPractitionerFiltered,
    documentTypeFiltered,
    perPage,
    idFiltered,
    applyFiltersEnabled,
    stagedChangeCount,
    visibleFilterCount,
  }: {
    patientFiltered?: boolean;
    patientAdvancedFiltered?: string[];
    servicesFiltered?: string;
    sourceFiltered?: string;
    // Plain strings rather than the enums: a multi-select filter reports its values comma-joined.
    statusFiltered?: string;
    mediumFiltered?: string;
    page?: number;
    dashboardType?: string;
    recipient?: string;
    dateRange?: DateRangeValue;
    defaultDateRange?: boolean;
    hideOtherRequesters?: boolean;
    signingPractitionerFiltered?: string;
    documentTypeFiltered?: string;
    perPage?: number;
    idFiltered?: string;
    /** Whether `cafe-doc-apply-filters` / `cafe-li-apply-filters` was on for this event. */
    applyFiltersEnabled?: boolean;
    /** How many filters the draft changed relative to what was applied, for an explicit Apply. */
    stagedChangeCount?: number;
    /** How many filters the bar shows, when the filter bar is customizable. */
    visibleFilterCount?: number;
  }): void {
    const props: Segments.DashboardFiltered = {
      patientFiltered,
      patientAdvancedFiltered,
      servicesFiltered,
      sourceFiltered,
      statusFiltered,
      mediumFiltered,
      page,
      dashboardType,
      recipient,
      dateRange,
      defaultDateRange,
      hideOtherRequesters,
      signingPractitionerFiltered,
      documentTypeFiltered,
      perPage,
      idFiltered,
      applyFiltersEnabled,
      stagedChangeCount,
      visibleFilterCount,
    };
    this.analytics.track(TrackingEventName.DashboardFiltered, props, this.options);
  }

  public dashboardSorted({
    dashboardType,
    sortBy,
    sortDirection,
  }: {
    dashboardType?: string;
    sortBy?: string;
    sortDirection?: string;
  }): void {
    const props: Segments.DashboardSorted = {
      dashboardType,
      sortBy,
      sortDirection,
    };
    this.analytics.track(TrackingEventName.DashboardSorted, props, this.options);
  }

  public inErrorsDashboardFiltered(searchParams: DashboardSearchParams): void {
    const props: Segments.InErrorsDashboardFiltered = {
      id: searchParams.id,
      fromDate: searchParams.fromDate,
      untilDate: searchParams.untilDate,
      message_contains: searchParams.message_contains,
      filename: searchParams.filename,
      patientFiltered:
        !!searchParams.firstname ||
        !!searchParams.lastname ||
        !!searchParams.ipp ||
        !!searchParams.birthdate,
    };
    this.analytics.track(TrackingEventName.InErrorsDashboardFiltered, props, this.options);
  }

  public documentTypeModified({
    newType,
    previousType,
  }: {
    newType: string;
    previousType: string;
  }): void {
    const props: Segments.DocumentTypeModified = {
      newType,
      previousType,
    };
    this.analytics.track(TrackingEventName.DocumentTypeModified, props, this.options);
  }

  public documentSpecialityModified({
    newSpeciality,
    previousSpeciality,
  }: {
    newSpeciality: string;
    previousSpeciality: string;
  }): void {
    const props: Segments.DocumentSpecialityModified = {
      newSpeciality,
      previousSpeciality,
    };
    this.analytics.track(TrackingEventName.DocumentSpecialityModified, props, this.options);
  }

  public documentForwardToLifenDocumentModified({
    forwardToLifenDocument,
  }: {
    forwardToLifenDocument: boolean;
  }): void {
    const props: Segments.DocumentForwardToLifenDocumentModified = {
      forwardToLifenDocument,
    };
    this.analytics.track(
      TrackingEventName.DocumentForwardToLifenDocumentModified,
      props,
      this.options,
    );
  }

  public documentDatePeriodModified({
    newStartDate,
    newEndDate,
    previousStartDate,
    previousEndDate,
  }: {
    newStartDate?: string;
    newEndDate?: string;
    previousStartDate?: string;
    previousEndDate?: string;
  }): void {
    const props: Segments.DocumentDatePeriodModified = {
      newStartDate,
      newEndDate,
      previousStartDate,
      previousEndDate,
    };
    this.analytics.track(TrackingEventName.DocumentDatePeriodModified, props, this.options);
  }

  public documentSenderModified({
    newSender,
    previousSender,
  }: {
    newSender: string;
    previousSender: string;
  }) {
    const props: Segments.DocumentSenderModified = {
      newSender,
      previousSender,
    };
    this.analytics.track(TrackingEventName.DocumentSenderModified, props, this.options);
  }

  public batchDocumentsModified(segment: Segments.BatchDocumentsModified) {
    this.analytics.track(TrackingEventName.BatchDocumentsModified, segment, this.options);
  }

  public documentTitleModified(segment: Segments.DocumentTitleModified) {
    this.analytics.track(TrackingEventName.DocumentTitleModified, segment, this.options);
  }

  public medicalExamTypeModified({
    newType,
    previousType,
  }: {
    newType: string | null;
    previousType: string | null;
  }): void {
    const props = {
      newType,
      previousType,
    };
    this.analytics.track(TrackingEventName.MedicalExamTypeModified, props, this.options);
  }

  public documentOriginalSenderModified({
    newValue,
    previousValue,
  }: {
    newValue: string | null;
    previousValue: string | null;
  }): void {
    const props = {
      newValue,
      previousValue,
    };
    this.analytics.track(TrackingEventName.DocumentOriginalSenderModified, props, this.options);
  }

  public patientUpdated({
    patientId,
    updatedBy,
    hasNir,
    matchStatus,
    editedFromRecipient,
  }: {
    patientId: string;
    updatedBy?: string;
    hasNir?: boolean;
    matchStatus?: PatientMatchStatus;
    editedFromRecipient?: boolean;
  }): void {
    const props: Segments.PatientUpdated = {
      patientId,
      updatedBy,
      hasNir,
      matchStatus,
      editedFromRecipient,
    };
    this.analytics.track(TrackingEventName.PatientUpdated, props, this.options);
  }

  public refreshPatientMatch({ matchStatus }: { matchStatus: PatientMatchStatus }): void {
    const props: Segments.PatientMatchRelaunched = { matchStatus };
    this.analytics.track(TrackingEventName.PatientMatchRelaunched, props, this.options);
  }

  public patientSearched(): void {
    this.analytics.track(TrackingEventName.PatientSearched, {}, this.options);
  }

  public documentPatientCreated({ patientId }: { patientId?: string }): void {
    const props: Segments.PatientCreated = { fhirReference: patientId };
    this.analytics.track(TrackingEventName.PatientCreated, props, this.options);
  }

  public patientCreateClicked(): void {
    this.analytics.track(TrackingEventName.PatientCreateClicked, {}, this.options);
  }

  public patientModifyClicked({ matchStatus }: { matchStatus: PatientMatchStatus }): void {
    const props: Segments.PatientModifyClicked = { matchStatus };
    this.analytics.track(TrackingEventName.PatientModifyClicked, props, this.options);
  }

  public documentPatientRemoved({
    patientId,
    matchStatus,
  }: {
    patientId: string;
    matchStatus: string;
  }): void {
    const props: Segments.PatientRemoved = { patientId, matchStatus };
    this.analytics.track(TrackingEventName.PatientRemoved, props, this.options);
  }

  public documentPatientAdded({ patientId }: { patientId: string }): void {
    const props: Segments.PatientAdded = { patientId };
    this.analytics.track(TrackingEventName.PatientAdded, props, this.options);
  }

  public batchActionClicked({
    actionType,
    numberOfSelectedRows,
    documentIds,
  }: {
    actionType: Segments.BatchActionClickedActionType;
    numberOfSelectedRows: number;
    documentIds: string[];
  }): void {
    const props: Segments.BatchActionClicked = { actionType, numberOfSelectedRows, documentIds };
    this.analytics.track(TrackingEventName.BatchActionClicked, props, this.options);
  }

  public documentActionClicked({
    actionType,
    communicationRequest,
  }: {
    actionType: Segments.DocumentActionClickedActionType;
    communicationRequest: string;
  }): void {
    const props: Segments.DocumentActionClicked = { actionType, communicationRequest };
    this.analytics.track(TrackingEventName.DocumentActionClicked, props, this.options);
  }

  public documentIDCopied({ fhirReference }: { fhirReference: string | undefined }) {
    const props: Segments.DocumentIDCopied = { fhirReference };
    this.analytics.track(TrackingEventName.DocumentIdCopied, props, this.options);
  }

  public documentIntegrationMatchDone({ fhirReference }: { fhirReference: string | undefined }) {
    const props: Segments.IntegrationMatchDone = { fhirReference };
    this.analytics.track(TrackingEventName.IntegrationMatchDone, props, this.options);
  }

  public groupedDocumentReordered({ fhirReference }: { fhirReference: string | undefined }) {
    const props: Segments.GroupedDocumentReordered = { fhirReference };
    this.analytics.track(TrackingEventName.GroupedDocumentReordered, props, this.options);
  }

  public documentPrinted({ fhirReference }: { fhirReference: string | undefined }) {
    const props: Segments.DocumentPrinted = { fhirReference };
    this.analytics.track(TrackingEventName.DocumentPrinted, props, this.options);
  }

  public pdfViewerLongPress() {
    this.analytics.track(TrackingEventName.PdfViewerLongPress, undefined, this.options);
  }

  public PdfEditorPageViewed(source: string) {
    const props: Segments.PDFEditorPageViewed = { source };
    this.analytics.track(TrackingEventName.PdfEditorPageViewed, props, this.options);
  }

  public pdfEditorBackButtonPressed() {
    this.analytics.track(TrackingEventName.PdfEditorBack, undefined, this.options);
  }

  public pdfEditorCancelButtonPressed() {
    this.analytics.track(TrackingEventName.PdfEditorCancel, undefined, this.options);
  }

  public pdfEditorCut() {
    this.analytics.track(TrackingEventName.PdfEditorCut, undefined, this.options);
  }

  public pdfEditorRemoveCut() {
    this.analytics.track(TrackingEventName.PdfEditorUncut, undefined, this.options);
  }

  public pdfEditorCutAll() {
    this.analytics.track(TrackingEventName.PdfEditorCutAll, undefined, this.options);
  }

  public pdfEditorRotatePage() {
    this.analytics.track(TrackingEventName.PdfEditorRotatePage, undefined, this.options);
  }

  public pdfEditorRemovePage() {
    this.analytics.track(TrackingEventName.PdfEditorRemovePage, undefined, this.options);
  }

  public pdfEditorRestorePage() {
    this.analytics.track(TrackingEventName.PdfEditorRestorePage, undefined, this.options);
  }

  public pdfEditorResetButtonPressed() {
    this.analytics.track(TrackingEventName.PdfEditorReset, undefined, this.options);
  }

  public pdfEditorSubmitButtonPressed() {
    this.analytics.track(TrackingEventName.PdfEditorSubmit, undefined, this.options);
  }

  public pdfEditorPagePreviewOpened() {
    this.analytics.track(TrackingEventName.PdfEditorZoom, undefined, this.options);
  }

  public pdfEditorPagePreviewNavigationClicked() {
    this.analytics.track(TrackingEventName.PdfEditorZoomNavigate, undefined, this.options);
  }

  public pdfEditorPermutatePages() {
    this.analytics.track(TrackingEventName.PdfEditorPermutatePages, undefined, this.options);
  }

  public pdfEditorAcceptSuggestion(action: SuggestionAction) {
    this.analytics.track(TrackingEventName.PdfEditorAcceptSuggestion, { action }, this.options);
  }

  public pdfEditorDismissSuggestion(action: SuggestionAction) {
    this.analytics.track(TrackingEventName.PdfEditorDismissSuggestion, { action }, this.options);
  }

  public importOriginalDocumentSubmitButtonClicked() {
    this.analytics.track(
      TrackingEventName.ImportOriginalDocumentSubmitted,
      undefined,
      this.options,
    );
  }

  public importOriginalDocumentBackButtonClicked() {
    this.analytics.track(TrackingEventName.ImportOriginalDocumentBack, undefined, this.options);
  }

  public importModalExpanded({ totalDocuments }: { totalDocuments: number }) {
    const props: Segments.ImportModalExpanded = { total_documents: totalDocuments };
    this.analytics.track(TrackingEventName.ImportModalExpanded, props, this.options);
  }

  public importModalClosed() {
    this.analytics.track(TrackingEventName.ImportModalClosed, {}, this.options);
  }

  public addToPendingFolderButtonClicked() {
    this.analytics.track(TrackingEventName.AddToPendingFolder, undefined, this.options);
  }

  public revertAddToPendingFolderButtonClicked() {
    this.analytics.track(TrackingEventName.RevertAddToPendingFolder, undefined, this.options);
  }

  public addToPendingFolderGoToListButtonClicked() {
    this.analytics.track(TrackingEventName.AddToPendingFolderGotoList, undefined, this.options);
  }

  public removeFromPendingFolderButtonClicked() {
    this.analytics.track(TrackingEventName.RemoveFromPendingFolder, undefined, this.options);
  }

  public revertRemoveFromPendingFolderButtonClicked() {
    this.analytics.track(TrackingEventName.RevertRemoveFromPendingFolder, undefined, this.options);
  }

  public removeFromPendingFolderGoToListButtonClicked() {
    this.analytics.track(
      TrackingEventName.RemoveFromPendingFolderGotoList,
      undefined,
      this.options,
    );
  }

  public documentCustomENSMessageAdded({
    documentId,
    isEmpty,
  }: {
    documentId: string | undefined;
    isEmpty: boolean;
  }) {
    const props: Segments.CustomEnsMessageAdded = { documentId, isEmpty };
    this.analytics.track(TrackingEventName.CustomEnsMessageAdded, props, this.options);
  }

  public documentCloseENSConversation({
    documentId,
    closed,
  }: {
    documentId: string | undefined;
    closed: boolean;
  }) {
    const props: Segments.CloseEnsConversation = { documentId, closed };
    this.analytics.track(TrackingEventName.CloseEnsConversation, props, this.options);
  }

  public newDematRecipientFormOpened() {
    this.analytics.track(TrackingEventName.NewDematRecipientFormOpened, undefined, this.options);
  }

  public newDematRecipientFormSubmitted({ addressType }: { addressType: TelecomType }) {
    const props: Segments.NewDematRecipientFormSubmitted = {
      address_type: addressType as unknown as Segments.AddressType,
    };
    this.analytics.track(TrackingEventName.NewDematRecipientFormSubmitted, props, this.options);
  }

  public newDematRecipientFormMatchExistingEmail(type: string, email: string, nbResults: number) {
    const props: Segments.NewDematRecipientFormMatchExistingEmail = {
      type,
      email,
      nbResults,
    };
    this.analytics.track(
      TrackingEventName.NewDematRecipientFormMatchExistingEmail,
      props,
      this.options,
    );
  }

  public newDematRecipientFormSubmitExistingEmail(type: string) {
    const props: Segments.NewDematRecipientFormSubmitExistingEmail = {
      type,
    };
    this.analytics.track(
      TrackingEventName.NewDematRecipientFormSubmitExistingEmail,
      props,
      this.options,
    );
  }

  public appIsOpened(currentApp: AppKeys) {
    if (this.currentAppOpened !== currentApp) {
      const defaultAtLaunch = this.currentAppOpened === undefined;
      this.currentAppOpened = currentApp;

      this.analytics.track('App Opened', {
        app: currentApp,
        openingMethod: defaultAtLaunch
          ? Segments.OpeningMethod.DefaultAtLaunch
          : Segments.OpeningMethod.UserSwitch,
      });
    }
  }

  public appUpdateFailed({ errorCause }: { errorCause?: string }): void {
    const props: Segments.AppUpdateFailed = {
      errorCause,
    };

    this.analytics.track(TrackingEventName.AppUpdateFailed, props, this.options);
  }

  public appUpdateStarted({
    desktopVersion,
    webVersion,
  }: {
    desktopVersion?: string;
    webVersion?: string;
  }): void {
    const props: Segments.AppUpdateStarted = {
      desktopVersion,
      webVersion,
    };

    this.analytics.track(TrackingEventName.AppUpdateStarted, props, this.options);
  }

  public appUpdateCompleted({
    desktopVersion,
    webVersion,
  }: {
    desktopVersion?: string;
    webVersion?: string;
  }): void {
    const props: Segments.AppUpdateCompleted = {
      desktopVersion,
      webVersion,
    };

    this.analytics.track(TrackingEventName.AppUpdateCompleted, props, this.options);
  }

  public selectedIdentityUpdated({ newSelectedIdentity }: { newSelectedIdentity?: string }): void {
    const props: Segments.SelectedIdentityUpdated = {
      newSelectedIdentity,
    };
    this.analytics.track(TrackingEventName.SelectedIdentityUpdated, props, this.options);
  }

  public documentEncounterListClicked(): void {
    this.analytics.track(TrackingEventName.EncounterListClicked, {}, this.options);
  }

  public documentEncounterUpdated(): void {
    this.analytics.track(TrackingEventName.EncounterUpdated, {}, this.options);
  }

  public documentNavigatorClicked({
    navigation,
    dashboardType,
  }: {
    navigation: 'next' | 'previous';
    dashboardType: string;
  }): void {
    const props: Segments.DocumentNavigatorClicked = { navigation, dashboardType };
    this.analytics.track(TrackingEventName.DocumentNavigatorClicked, props, this.options);
  }

  public userForcedLogout(): void {
    this.analytics.track(TrackingEventName.UserForcedLogout, {}, this.options);
  }

  public trackVerificationAction(props: Segments.VerificationAction): void {
    this.analytics.track(TrackingEventName.VerificationAction, props, this.options);
  }

  public trackDirectorySearched(searchText: string, type: ProfessionalBaseType): void {
    this.analytics.track(TrackingEventName.DirectorySearched, { searchText, type }, this.options);
  }

  public trackDirectorySearchPanelsClicked(panelType: Segments.PanelType): void {
    this.analytics.track(
      TrackingEventName.DirectorySearchPanelsClicked,
      { panelType },
      this.options,
    );
  }

  public trackDirectoryResultClicked(
    resultType?: 'practitioner' | 'organization',
    index?: number,
  ): void {
    if (!resultType || !index) {
      return;
    }

    this.analytics.track(
      TrackingEventName.DirectoryResultClicked,
      { resultType, index },
      this.options,
    );
  }

  public trackOpenConnectorSettings() {
    this.analytics.track(TrackingEventName.OpenConnectorSettings, this.options);
  }

  public trackSecretConnectionChanged(value: boolean) {
    this.analytics.track(TrackingEventName.SecretConnectionChanged, { value }, this.options);
  }

  public trackConnectorSecurityLabelChanged(securityLabels: string[]) {
    this.analytics.track(
      TrackingEventName.ConnectorSecurityLabelChanged,
      { securityLabels },
      this.options,
    );
  }
  public trackNetworkConnection(downlink: number): void {
    this.analytics.track(TrackingEventName.NetworkConnection, { downlink }, this.options);
  }

  public cancelDocumentsSending({ documentIds }: { documentIds: string[] }): void {
    const props: Segments.CancelDocumentsSending = { documentIds };
    this.analytics.track(TrackingEventName.CancelDocumentsSending, props, this.options);
  }

  public medicalVisaCommentClicked(): void {
    this.analytics.track(TrackingEventName.MedicalVisaCommentClicked, {}, this.options);
  }

  public medicalVisaDashboardFiltered(searchParams: MedicalVisaQuery): void {
    const props: Segments.MedicalVisaDashboardFiltered = {
      // The Segment event declares one string: a multi-value selection is its values joined by a comma.
      documentType: joinValues(searchParams.filters.documentType),
      page: searchParams.page,
      pageSize: searchParams.pageSize,
      status: searchParams.filters.status?.[0], // only one status can be selected
      orderBy: searchParams.orderBy,
      orderDirection: searchParams.orderDirection,
    };

    this.analytics.track(TrackingEventName.MedicalVisaDashboardFiltered, props, this.options);
  }

  public viewPendingReadyToIntegrateDocumentsClicked() {
    this.analytics.track(
      TrackingEventName.ViewPendingReadyToIntegrateDocumentsClicked,
      undefined,
      this.options,
    );
  }

  public searchPatientStarted({
    targetId,
    filledFields,
    AiPrefilledFields,
  }: Segments.SearchPatientStarted) {
    const properties = {
      targetId,
      filledFields,
      AiPrefilledFields,
    };
    this.analytics.track(TrackingEventName.SearchPatientStarted, properties, this.options);
  }
  public applyPatientResult({ targetId, resultPosition }: Segments.ApplyPatientResult) {
    const properties = {
      targetId,
      resultPosition,
    };
    this.analytics.track(TrackingEventName.ApplyPatientResult, properties, this.options);
  }

  public medicalVisaSigned({ documentType, withComment }: Segments.MedicalVisaSigned): void {
    const props = {
      documentType,
      withComment,
    };

    this.analytics.track(TrackingEventName.MedicalVisaSigned, props, this.options);
  }

  public trackPusherDisconnected({ state }: { state: string }): void {
    this.analytics.track(TrackingEventName.PusherDisconnected, { state }, this.options);
  }

  public trackPusherReconnected({ downtimeMs }: { downtimeMs: number }): void {
    this.analytics.track(TrackingEventName.PusherReconnected, { downtimeMs }, this.options);
  }

  public trackPusherEventMissed({
    documentId,
    detectedAfterMs,
  }: {
    documentId: string;
    detectedAfterMs: number;
  }): void {
    this.analytics.track(
      TrackingEventName.PusherEventMissed,
      { documentId, detectedAfterMs },
      this.options,
    );
  }

  public trackAnalysisPollingGaveUp({ documentId }: { documentId: string }): void {
    this.analytics.track(TrackingEventName.AnalysisPollingGaveUp, { documentId }, this.options);
  }
}

export const tracking = new Tracking(new AnalyticsBrowser());
tracking.bindUploadTracking();
